import { type KnownUser } from '@databases/types/confluence';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { getIdentity, synchroniseIdentity } from '@canvas/connection/identity';

import { getBulkUserByIds } from '../../confluence/user';
import { getThrottledBatchCallback, serializeError } from '../../utils';
import { type CommonRootState, loadAsync } from '../redux';

import { selectUserByIdOptional, selectUserByIdStatus } from './selectors';
import usersSlice from './slice';

const getUserById = getThrottledBatchCallback<[string], KnownUser | undefined>(
  async (args) => {
    const accountIds = args.map((arg) => arg[0]);

    const result = await getBulkUserByIds(accountIds);

    return accountIds.map((accountId) =>
      result.find((user) => user.accountId === accountId)
    );
  }
);

type Dispatcher<Result> = ThunkAction<Result, CommonRootState, void, AnyAction>;

/**
 * Load the specified users into the store. If some of the users are already loaded, no request is made for them.
 */
export const loadUserById =
  (
    accountId: string,
    {
      forceReload = false
    }: {
      forceReload?: boolean;
    } = {}
  ): Dispatcher<Promise<KnownUser | undefined>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectUserByIdStatus(getState(), { accountId }),
      () => {
        dispatch(usersSlice.actions.loadUserByIdPending({ accountId }));
      },
      async () =>
        (!forceReload && selectUserByIdOptional(getState(), { accountId })) ||
        (await getUserById(accountId)),
      (user) => {
        if (user) {
          dispatch(addUserToList(user));
        }
        dispatch(usersSlice.actions.loadUserByIdFinished({ accountId }));
      },
      (error) => {
        dispatch(
          usersSlice.actions.loadUserByIdError({
            accountId,
            error: serializeError(error)
          })
        );
      }
    );

    return selectUserByIdOptional(getState(), { accountId });
  };

export const loadCurrentUser =
  (currentUserId: string): Dispatcher<Promise<KnownUser | undefined>> =>
  async (dispatch, getState) => {
    await loadAsync(
      () => selectUserByIdStatus(getState(), { accountId: currentUserId }),
      () => {
        dispatch(usersSlice.actions.loadUserByIdPending({ accountId: currentUserId }));
      },
      async () => {
        let identity = await getIdentity();

        if (identity.accountId !== currentUserId) {
          await synchroniseIdentity();
          identity = await getIdentity();
        }

        return identity;
      },
      (user) => {
        if (user) {
          dispatch(
            addUserToList({
              accountId: user.accountId,
              displayName: user.nickname,
              type: 'known',
              profilePicture: {
                path: user.picture
              }
            })
          );
        }
        dispatch(usersSlice.actions.loadUserByIdFinished({ accountId: currentUserId }));
      },
      (error) => {
        dispatch(
          usersSlice.actions.loadUserByIdError({
            accountId: currentUserId,
            error: serializeError(error)
          })
        );
      }
    );

    return selectUserByIdOptional(getState(), { accountId: currentUserId });
  };

/**
 * Add a new user to the list of users. If a user with the same account ID exists already, it is replaced.
 */
export const addUserToList = (user: KnownUser): AnyAction => addUsersToList([user]);

export const addUsersToList = (users: Array<KnownUser>): AnyAction =>
  usersSlice.actions.addUsersToList({
    users
  });
